import React, { useState } from 'react';
import { Terminal, Copy, Check, FileCode } from 'lucide-react';
import type { ApiDocumentation, ApiDocGroup, ApiDocEndpoint } from '../../../../shared/types/index.js';
import { EndpointItem } from './EndpointItem.js';

interface ApiEndpointsTabProps {
  docs: ApiDocumentation;
  token?: string | null;
}

export const ApiEndpointsTab: React.FC<ApiEndpointsTabProps> = ({ docs, token }) => {
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
  const [expandedEndpoints, setExpandedEndpoints] = useState<Record<string, boolean>>({});

  const toggleEndpoint = (key: string) => {
    setExpandedEndpoints((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(id);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Auth & Environment Info Box */}
      <div className="bg-slate-900 text-slate-200 p-5 rounded-2xl shadow-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-indigo-400" />
            <span className="text-xs font-bold text-slate-100 uppercase tracking-wider">
              Format Autentikasi JWT Bearer
            </span>
          </div>
          <span className="text-[11px] text-emerald-400 font-mono">Status Server: 200 OK</span>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed">
          Kirim token JWT pada header setiap permintaan API yang membutuhkan hak akses terautentikasi:
        </p>

        <div className="p-3 bg-slate-950 rounded-xl font-mono text-xs text-indigo-300 flex items-center justify-between border border-slate-800">
          <span className="truncate mr-2">
            Authorization: Bearer {token ? `${token.substring(0, 32)}...` : '<TOKEN_JWT>'}
          </span>
          <button
            onClick={() => copyToClipboard(`Authorization: Bearer ${token}`, 'auth-header')}
            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium flex items-center gap-1 shrink-0 cursor-pointer"
          >
            {copiedIndex === 'auth-header' ? (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            <span>{copiedIndex === 'auth-header' ? 'Disalin' : 'Salin Header'}</span>
          </button>
        </div>
      </div>

      {/* Endpoints Groups */}
      <div className="space-y-6">
        {(docs?.endpoints || []).map((group: ApiDocGroup, gIdx: number) => (
          <div key={gIdx} className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="p-4 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <FileCode className="w-4 h-4 text-indigo-600" />
                <span>{group.tag}</span>
              </h2>
              <span className="text-xs text-slate-400 font-medium">
                {(group.endpoints || []).length} endpoint
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {(group.endpoints || []).map((ep: ApiDocEndpoint, eIdx: number) => {
                const epKey = `${ep.method}-${ep.path}`;
                const isExpanded = !!expandedEndpoints[epKey];

                return (
                  <EndpointItem
                    key={eIdx}
                    endpoint={ep}
                    isExpanded={isExpanded}
                    token={token}
                    copiedIndex={copiedIndex}
                    onToggle={() => toggleEndpoint(epKey)}
                    onCopy={copyToClipboard}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
